
import React from 'react';
import { McpToolPolicy, ChannelFormData } from '../types';
import { DeleteButton } from '../../shared/DeleteButton';

export interface PoliciesTabProps {
  formData: ChannelFormData;
  setFormData: React.Dispatch<React.SetStateAction<ChannelFormData>>;
  setIsModified: (modified: boolean) => void;
  opaPolicyValid: boolean;
  opaPolicyErrors: string[];
  isValidatingPolicy: boolean;
  editingPolicyId: string | null;
  setEditingPolicyId: (id: string | null) => void;
  showPolicyEditor: boolean;
  setShowPolicyEditor: (show: boolean) => void;
  policyName: string;
  setPolicyName: (name: string) => void;
  policyDescription: string;
  setPolicyDescription: (description: string) => void;
  policyCode: string;
  setPolicyCode: (code: string) => void;
  policyEnforce: boolean;
  setPolicyEnforce: (enforce: boolean) => void;
  policyPriority: number;
  setPolicyPriority: (priority: number) => void;
  isValidatingToolPolicy: boolean;
  toolPolicyValid: boolean;
  toolPolicyErrors: string[];
}

const PoliciesTab: React.FC<PoliciesTabProps> = ({
  formData,
  setFormData,
  setIsModified,
  opaPolicyValid,
  opaPolicyErrors,
  isValidatingPolicy,
  editingPolicyId,
  setEditingPolicyId,
  showPolicyEditor,
  setShowPolicyEditor,
  policyName,
  setPolicyName,
  policyDescription,
  setPolicyDescription,
  policyCode,
  setPolicyCode,
  policyEnforce,
  setPolicyEnforce,
  policyPriority,
  setPolicyPriority,
  isValidatingToolPolicy,
  toolPolicyValid,
  toolPolicyErrors,
}) => {
  const opaEnabled = formData.opa_policy_config?.enabled || false;
  const mcpToolPoliciesEnabled = formData.mcp_tool_policies_config?.enabled || false;

  return (
    <>

      {/* Channel-Level OPA Policy Section - All Protocols */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-shield-alt"></i> Channel-Level OPA Policy
            </h6>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="opa-enabled"
                checked={opaEnabled}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      opa_policy_config: { enabled: true, policy: '' }
                    });
                  } else {
                    setFormData({
                      ...formData,
                      opa_policy_config: undefined
                    });
                  }
                  setIsModified(true);
                }}
              />
              <label className="form-check-label" htmlFor="opa-enabled" style={{ color: opaEnabled ? '#000' : '#6c757d' }}>
                Enabled
              </label>
            </div>
          </div>
        </div>
        {opaEnabled && (
          <div className="card-body">
            <p className="text-muted small mb-3">
              <b><i>Rego</i></b> is a declarative, purpose-built query language enabling policy authors to define rules and conditions for what is allowed or denied based on structured input data such as JSON or YAML. It emphasizes a declarative approach, focusing on the desired outcome rather than execution details, allowing the Open Policy Agent (OPA) to optimize performance. Designed for complex, hierarchical data structures common in cloud-native environments, Rego treats policies as code, making them version-controlled, testable, and reviewable like application code.
              {formData.protocol === 'mcp' 
                ? ' The policies you define here can filter MCP tools based on JWT claims and request context.'
                : ' The policies you define here control access to your channel based on JWT claims and request context.'}
            </p>

            {/* Policy Instructions Info Card */}
            <div className="card shadow-sm mb-4 border-left-primary">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="font-weight-bold text-primary mb-0">
                    <i className="fas fa-graduation-cap mr-2"></i>
                    Policy Instructions & Examples
                  </h6>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    type="button"
                    data-toggle="collapse"
                    data-target="#policyInstructions"
                    aria-expanded="false"
                  >
                    <i className="fas fa-chevron-down"></i>
                  </button>
                </div>
                
                <div className="collapse" id="policyInstructions">
                  <p className="text-gray-800 mb-3">
                    Learn how to write Rego policies to control access to MCP tools based on JWT claims, roles, and request context.
                  </p>
                  
                  <div className="card bg-light border-0">
                    <div className="card-body">
                      <h6 className="font-weight-bold text-dark mb-3">
                        <i className="fas fa-lightbulb mr-2"></i>
                        Policy Structure
                      </h6>
                      <p className="mb-3 text-gray-700">All policies must use the <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px', color: '#d63384'}}>channel.policy</code> package and define an <code style={{backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px', color: '#d63384'}}>allow</code> rule:</p>
                      
                      <pre className="p-3 rounded mb-3" style={{fontSize: '0.85rem', backgroundColor: '#f8f9fa', color: '#212529', border: '1px solid #dee2e6'}}><code style={{color: '#212529'}}>{`package channel.policy

# Default decision (true = allow all, false = deny all)
default allow = false

# Rules that evaluate to true allow the request
allow {
  # Your conditions here
}`}</code></pre>

                      <h6 className="font-weight-bold text-dark mb-3 mt-4">
                        <i className="fas fa-database mr-2"></i>
                        Available Input Context
                      </h6>
                      <p className="mb-2 text-gray-700">Your policies can access:</p>
                      <ul className="mb-3 text-gray-800">
                        <li><strong>input.mcp.method</strong> - MCP method (e.g., "tools/call")</li>
                        <li><strong>input.mcp.tool_name</strong> - Name of the tool being invoked</li>
                        <li><strong>input.mcp.params</strong> - Request parameters</li>
                        <li><strong>input.jwt.*</strong> - All JWT claims (sub, role, subscription, etc.)</li>
                      </ul>

                      <h6 className="font-weight-bold text-dark mb-3 mt-4">
                        <i className="fas fa-code-branch mr-2"></i>
                        Example 1: Allow All Tools
                      </h6>
                      <pre className="p-3 rounded mb-3" style={{fontSize: '0.85rem', backgroundColor: '#f8f9fa', color: '#212529', border: '1px solid #dee2e6'}}><code style={{color: '#212529'}}>{`package channel.policy

default allow = true

allow_tool(tool_name) {
  true
}`}</code></pre>

                      <h6 className="font-weight-bold text-dark mb-3 mt-4">
                        <i className="fas fa-user-shield mr-2"></i>
                        Example 2: Admin-Only Tools
                      </h6>
                      <pre className="p-3 rounded mb-3" style={{fontSize: '0.85rem', backgroundColor: '#f8f9fa', color: '#212529', border: '1px solid #dee2e6'}}><code style={{color: '#212529'}}>{`package channel.policy

default allow = false

# Admins get everything
allow {
  input.jwt.role == "admin"
}

# Regular users get safe tools only
allow {
  input.jwt.sub  # Authenticated
  input.mcp.tool_name
  safe_tool(input.mcp.tool_name)
}

safe_tool("get_weather") = true
safe_tool("search_web") = true
safe_tool("read_file") = true`}</code></pre>

                      <h6 className="font-weight-bold text-dark mb-3 mt-4">
                        <i className="fas fa-star mr-2"></i>
                        Example 3: Subscription Tiers
                      </h6>
                      <pre className="p-3 rounded mb-3" style={{fontSize: '0.85rem', backgroundColor: '#f8f9fa', color: '#212529', border: '1px solid #dee2e6'}}><code style={{color: '#212529'}}>{`package channel.policy

default allow = false

# Premium users: all tools
allow {
  input.jwt.subscription == "premium"
  input.mcp.tool_name
}

# Free users: basic tools only
allow {
  input.jwt.subscription == "free"
  input.mcp.tool_name
  basic_tool(input.mcp.tool_name)
}

basic_tool("search") = true
basic_tool("summarize") = true
basic_tool("translate") = true`}</code></pre>

                      <h6 className="font-weight-bold text-dark mb-3 mt-4">
                        <i className="fas fa-clock mr-2"></i>
                        Example 4: Time-Based Access
                      </h6>
                      <pre className="p-3 rounded mb-3" style={{fontSize: '0.85rem', backgroundColor: '#f8f9fa', color: '#212529', border: '1px solid #dee2e6'}}><code style={{color: '#212529'}}>{`package channel.policy

default allow = false

# Only during business hours (9 AM - 5 PM UTC)
allow {
  time.clock(time.now_ns(), "UTC")[0] >= 9
  time.clock(time.now_ns(), "UTC")[0] < 17
  input.mcp.tool_name
}`}</code></pre>

                      <div className="alert alert-info mb-3 mt-4">
                        <small className="text-dark">
                          <i className="fas fa-info-circle mr-2 text-info"></i>
                          <strong>Testing:</strong> Test your policies at <a href="https://play.openpolicyagent.org/" target="_blank" rel="noopener noreferrer" className="text-primary">play.openpolicyagent.org</a> before deploying.
                        </small>
                      </div>

                      <div className="alert alert-warning mb-0">
                        <small className="text-dark">
                          <i className="fas fa-exclamation-triangle mr-2 text-warning"></i>
                          <strong>Best Practice:</strong> Start with <code className="text-dark">default allow = false</code> and explicitly allow what you need for better security.
                        </small>
                      </div>

                      <div className="alert alert-success mb-0 mt-3">
                        <small className="text-dark">
                          <i className="fas fa-book mr-2 text-success"></i>
                          <strong>Resources:</strong> 
                          <a href="https://www.openpolicyagent.org/docs/latest/" target="_blank" rel="noopener noreferrer" className="ml-2">OPA Documentation</a> | 
                          <a href="https://www.openpolicyagent.org/docs/latest/policy-language/" target="_blank" rel="noopener noreferrer" className="ml-2">Rego Language Guide</a> | 
                          <a href="https://www.openpolicyagent.org/docs/latest/policy-reference/" target="_blank" rel="noopener noreferrer" className="ml-2">Built-in Functions</a>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Policy Content (Rego)
                {isValidatingPolicy && (
                  <span className="ml-2 text-muted small">
                    <i className="fas fa-spinner fa-spin"></i> Validating...
                  </span>
                )}
                {!isValidatingPolicy && (formData.opa_policy_config?.policy || '').trim() && opaPolicyValid && (
                  <span className="ml-2 text-success small">
                    <i className="fas fa-check-circle"></i> Valid
                  </span>
                )}
              </label>
              <textarea
                className={`form-control font-monospace small ${opaPolicyValid ? '' : 'is-invalid'}`}
                rows={12}
                style={{ fontSize: '0.875rem' }}
                value={formData.opa_policy_config?.policy || `package channel.policy

# Allow all tools by default
allow_tool(tool_name) {
    true
}`}

                onChange={(e) => {
                  setFormData({
                    ...formData,
                    opa_policy_config: {
                      ...formData.opa_policy_config!,
                      policy: e.target.value
                    }
                  });
                  setIsModified(true);
                }}
                placeholder={`...add policy here...`}
              />
              {opaPolicyErrors.length > 0 && (
                <div className="invalid-feedback d-block">
                  {opaPolicyErrors.map((err, idx) => (
                    <div key={idx}>{err}</div>
                  ))}
                </div>
              )}
              {!isValidatingPolicy && (formData.opa_policy_config?.policy || '').trim() && opaPolicyValid && (
                <small className="form-text text-success">
                  <i className="fas fa-check-circle"></i> Policy syntax is valid
                </small>
              )}
              <small className="form-text text-muted">
                Leave empty to allow all tools. Policy is evaluated with context including JWT claims, MCP method, and tool names.
              </small>
            </div>
          </div>
        )}
      </div>

      {/* MCP Tool Policies Section - MCP Only */}
      {(formData.protocol === 'mcp' || formData.protocol === 'mpx') && (
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="m-0 font-weight-bold text-primary">
                <i className="fas fa-tools"></i> MCP Tool Policies
              </h6>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="mcp-tool-policies-enabled"
                  checked={mcpToolPoliciesEnabled}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        mcp_tool_policies_config: { enabled: true, policies: [] }
                      });
                    } else {
                      setFormData({
                        ...formData,
                        mcp_tool_policies_config: {
                          ...(formData.mcp_tool_policies_config || { policies: [] }),
                          enabled: false
                        }
                      });
                    }
                    setIsModified(true);
                  }}
                />
                <label className="form-check-label" htmlFor="mcp-tool-policies-enabled" style={{ color: mcpToolPoliciesEnabled ? '#000' : '#6c757d' }}>
                  Enabled
                </label>
              </div>
            </div>
          </div>
          {mcpToolPoliciesEnabled && (
            <div className="card-body">
              <p className="text-muted small mb-3">
                Define fine-grained access control policies for individual MCP tools. Each policy can allow or deny specific tools based on JWT claims, request context, and parameters.
              </p>

              {/* Policy List */}
              {(formData.mcp_tool_policies_config?.policies || []).length > 0 && (
                <div className="mb-3">
                  {[...(formData.mcp_tool_policies_config?.policies || [])].sort((a, b) => a.priority - b.priority).map((policy) => (
                    <div key={policy.id} className="card mb-2 border" style={{ borderLeft: '4px solid #4e73df' }}>
                      <div className="card-body p-3">
                        <div className="row align-items-center">
                          <div className="col-auto">
                            <span className="badge" style={{ fontSize: '0.9rem', minWidth: '40px', backgroundColor: '#5a5c69', color: 'white' }}>
                              {policy.priority}
                            </span>
                          </div>
                          <div className="col">
                            <h6 className="mb-1" style={{ fontWeight: 600, color: '#2e3338' }}>{policy.name}</h6>
                            <p className="small mb-0" style={{ color: '#6c757d' }}>{policy.description || 'No description'}</p>
                          </div>
                          <div className="col-auto">
                            {policy.enforce ? (
                              <span className="badge" style={{ backgroundColor: '#018722ff', color: 'white' }}>
                                <i className="fas fa-check-circle me-1"></i> Enforced
                              </span>
                            ) : (
                              <span className="badge" style={{ backgroundColor: '#adadadff', color: '#2e3338' }}>
                                <i className="fas fa-exclamation-triangle me-1"></i> Not Enforced
                              </span>
                            )}
                          </div>
                          <div className="col-auto">
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => {
                                setEditingPolicyId(policy.id);
                                setPolicyName(policy.name);
                                setPolicyDescription(policy.description);
                                setPolicyCode(policy.policy);
                                setPolicyEnforce(policy.enforce);
                                setPolicyPriority(policy.priority);
                                setShowPolicyEditor(true);
                              }}
                            >
                              <i className="fas fa-edit me-1"></i> Edit
                            </button>
                            <DeleteButton
                              onDelete={() => {
                                setFormData({
                                  ...formData,
                                  mcp_tool_policies_config: {
                                    ...(formData.mcp_tool_policies_config || { enabled: false, policies: [] }),
                                    policies: (formData.mcp_tool_policies_config?.policies || []).filter(p => p.id !== policy.id)
                                  }
                                });
                                setIsModified(true);
                              }}
                              className="btn-sm"
                              title="Delete this policy"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* No policies message */}
              {(formData.mcp_tool_policies_config?.policies || []).length === 0 && (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  No tool policies defined. Click "Add Policy" to create your first policy.
                </div>
              )}

              {/* Policy Editor - Inline Form */}
              {showPolicyEditor && (
                <div className="card mb-3 border-primary">
                  <div className="card-header bg-primary bg-opacity-10">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        <i className="fas fa-code me-2"></i>
                        {editingPolicyId ? 'Edit Policy' : 'Add New Policy'}
                      </h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setShowPolicyEditor(false);
                          setEditingPolicyId(null);
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Policy Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={policyName}
                        onChange={(e) => setPolicyName(e.target.value)}
                        placeholder="e.g., Admin Only Tools"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={policyDescription}
                        onChange={(e) => setPolicyDescription(e.target.value)}
                        placeholder="e.g., Restrict certain tools to admin users"
                      />
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Priority</label>
                        <input
                          type="number"
                          className="form-control"
                          value={policyPriority}
                          onChange={(e) => setPolicyPriority(parseInt(e.target.value) || 100)}
                          min="0"
                        />
                        <small className="form-text text-muted">
                          Lower numbers evaluated first (0 = highest priority)
                        </small>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Enforcement</label>
                        <div className="form-check mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="policy-enforce"
                            checked={policyEnforce}
                            onChange={(e) => setPolicyEnforce(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="policy-enforce">
                            Enforce this policy
                          </label>
                        </div>
                        <small className="form-text text-muted">
                          When unchecked, policy is logged but not enforced
                        </small>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Policy Code (Rego)
                        {isValidatingToolPolicy && (
                          <span className="ms-2 text-muted small">
                            <i className="fas fa-spinner fa-spin"></i> Validating...
                          </span>
                        )}
                        {!isValidatingToolPolicy && policyCode.trim() && toolPolicyValid && (
                          <span className="ms-2 text-success small">
                            <i className="fas fa-check-circle"></i> Valid
                          </span>
                        )}
                      </label>
                      <textarea
                        className={`form-control font-monospace small ${toolPolicyValid ? '' : 'is-invalid'}`}
                        style={{ 
                          fontSize: '0.875rem',
                          minHeight: '300px',
                          resize: 'vertical'
                        }}
                        value={policyCode}
                        onChange={(e) => setPolicyCode(e.target.value)}
                        placeholder="Enter Rego policy code..."
                      />
                      {toolPolicyErrors.length > 0 && (
                        <div className="invalid-feedback d-block">
                          {toolPolicyErrors.map((err, idx) => (
                            <div key={idx}>{err}</div>
                          ))}
                        </div>
                      )}
                      {!isValidatingToolPolicy && policyCode.trim() && toolPolicyValid && (
                        <small className="form-text text-success">
                          <i className="fas fa-check-circle"></i> Policy syntax is valid
                        </small>
                      )}
                      <small className="form-text text-muted">
                        See the Channel-level OPA Policy section above for examples and documentation.
                      </small>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowPolicyEditor(false);
                          setEditingPolicyId(null);
                        }}
                      >
                        <i className="fas fa-times me-2"></i> Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          if (!policyName.trim()) {
                            alert('Please enter a policy name');
                            return;
                          }
                          if (!policyCode.trim()) {
                            alert('Please enter policy code');
                            return;
                          }

                          const newPolicy: McpToolPolicy = {
                            id: editingPolicyId || `policy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                            name: policyName,
                            description: policyDescription,
                            policy: policyCode,
                            enforce: policyEnforce,
                            priority: policyPriority
                          };

                          if (editingPolicyId) {
                            // Update existing
                            setFormData({
                              ...formData,
                              mcp_tool_policies_config: {
                                ...(formData.mcp_tool_policies_config || { enabled: false, policies: [] }),
                                policies: (formData.mcp_tool_policies_config?.policies || []).map(p => 
                                  p.id === newPolicy.id ? newPolicy : p
                                )
                              }
                            });
                          } else {
                            // Add new
                            setFormData({
                              ...formData,
                              mcp_tool_policies_config: {
                                ...(formData.mcp_tool_policies_config || { enabled: false, policies: [] }),
                                policies: [...(formData.mcp_tool_policies_config?.policies || []), newPolicy]
                              }
                            });
                          }

                          setIsModified(true);
                          setShowPolicyEditor(false);
                          setEditingPolicyId(null);
                        }}
                      >
                        <i className="fas fa-save me-2"></i> {editingPolicyId ? 'Update Policy' : 'Add Policy'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Policy Button */}
              {!showPolicyEditor && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingPolicyId(null);
                    setPolicyName('');
                    setPolicyDescription('');
                    setPolicyCode(`package channel.policy

default allow = false

# Add your policy rules here!
allow {
  input.jwt.sub  # Authenticated user
  input.mcp.tool_name
  # Add conditions
}`);
                    setPolicyEnforce(true);
                    setPolicyPriority(100);
                    setShowPolicyEditor(true);
                  }}
                >
                  <i className="fas fa-plus me-2"></i>Add Policy
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="alert alert-info">
        <i className="fas fa-lightbulb mr-2"></i>
        <strong>Tip:</strong> {formData.protocol === 'mcp' 
          ? 'Both policy types work together. The channel-level policy runs first (broad authorization), then MCP tool policies run (fine-grained control).' 
          : 'Use OPA policies to control access based on JWT claims, request context, and authentication attributes.'}
      </div>

    </>
  );
};

export default PoliciesTab;
